/**
 * A character, as a React element: the display's painted V12 child (see
 * painted.ts), head-and-shoulders by default, or whole in `mode="full"`.
 * Every face breathes and blinks on the page's one motion clock.
 *
 * `render` returns an SVG string, and this puts it in the page. The string is
 * built entirely from small integers and fixed shapes -- the only text that
 * can reach it is `title`, which `render` escapes -- so there is nothing in it
 * a person typed that could become markup. That is what makes the innerHTML
 * below safe, and it is worth keeping true: do not add a free-text part.
 */

import { useEffect } from 'react';
import { catalogue, normalise, type AvatarSpec } from './avatar';
import { startMotion } from './painted';
import { usePaintedSvg } from './painting';

export function Character({
  spec,
  mode = 'head',
  size,
  title,
  className,
  activity,
}: {
  spec: Partial<AvatarSpec> | null | undefined;
  mode?: 'head' | 'full';
  size?: number;
  /** Read out by a screen reader. Without one the drawing is decorative. */
  title?: string;
  className?: string;
  /** In `mode="full"`, which painting: 'standing' for a plain portrait. */
  activity?: string;
}) {
  useEffect(() => startMotion(), []);
  const svg = usePaintedSvg(spec, {
    ...(size ? { size } : {}),
    ...(title ? { title } : {}),
    ...(activity ? { activity } : {}),
    headOnly: mode === 'head',
  });
  // A face sits in a circle ringed in the colour of their clothes
  // (2026-10-13), so each child's ring is theirs wherever their face is.
  const head = mode === 'head';
  const ring = head && spec ? catalogue.shirtColor!.swatches![normalise(spec).shirtColor] : undefined;
  const classes = ['avatar-box', head ? 'avatar-head' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <span
      className={classes}
      style={
        ring
          ? ({ ['--shirt' as string]: ring, ['--ring' as string]: size && size < 40 ? '2px' : '3px' } as React.CSSProperties)
          : undefined
      }
      // eslint-disable-next-line react/no-danger -- see the note at the top
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
